/**
 * src/shell/auth/services/InviteCodeRegistry.ts
 *
 * Single-Use Invite Code & Ambassador Delegation Registry.
 * Enforces one-time usage, role binding, creator attribution,
 * and Superadmin ambassador quota delegations.
 */

import {
  InviteCode,
  InviteValidationResult,
  MeshUserRecord,
  AmbassadorQuota,
} from '../types/invite-types';
import { UserRole } from '../../../main/security/RoleEnforcement';
import { auditLogger } from '../../../platform/observability/audit-logger';

export class InviteCodeRegistry {
  private static instance: InviteCodeRegistry;
  private invites: Map<string, InviteCode> = new Map();
  private users: Map<string, MeshUserRecord> = new Map();
  private ambassadorQuotas: Map<string, AmbassadorQuota> = new Map();

  private constructor() {
    this.seedInitialData();
  }

  public static getInstance(): InviteCodeRegistry {
    if (!InviteCodeRegistry.instance) {
      InviteCodeRegistry.instance = new InviteCodeRegistry();
    }
    return InviteCodeRegistry.instance;
  }

  private seedInitialData(): void {
    // Seed initial users
    this.users.set('admin_1', {
      userId: 'admin_1',
      displayName: 'Superadmin Genesis',
      email: 'genesis@mesh.local',
      role: 'superadmin',
      isAmbassador: true,
      status: 'ACTIVE',
      authProviders: ['webauthn', 'google'],
      registeredAt: Date.now() - 86400000 * 30,
    });

    this.users.set('ambassador_1', {
      userId: 'ambassador_1',
      displayName: 'Elena Rostova (Ambassador)',
      email: 'elena@mesh.local',
      role: 'ordinary_user',
      isAmbassador: true,
      ambassadorQuota: {
        userId: 'ambassador_1',
        isAmbassador: true,
        totalQuota: 5,
        usedCount: 1,
        remainingCount: 4,
        grantedBy: 'admin_1',
        grantedAt: Date.now() - 86400000 * 5,
      },
      status: 'ACTIVE',
      authProviders: ['webauthn', 'email_ota'],
      registeredAt: Date.now() - 86400000 * 10,
    });

    this.users.set('user_regular', {
      userId: 'user_regular',
      displayName: 'Marcus Chen',
      email: 'marcus@mesh.local',
      role: 'ordinary_user',
      isAmbassador: false,
      status: 'ACTIVE',
      authProviders: ['google'],
      registeredAt: Date.now() - 86400000 * 2,
    });

    // Seed initial invite codes
    this.invites.set('SOVEREIGN-BETA-2026', {
      code: 'SOVEREIGN-BETA-2026',
      status: 'UNUSED',
      createdBy: 'admin_1',
      creatorRole: 'superadmin',
      boundRole: 'mesh_owner',
      createdAt: Date.now(),
      expiresAt: Date.now() + 86400000 * 14,
      maxUses: 1,
    });

    this.invites.set('AMBASSADOR-INVITE-01', {
      code: 'AMBASSADOR-INVITE-01',
      status: 'UNUSED',
      createdBy: 'ambassador_1',
      creatorRole: 'ambassador',
      createdAt: Date.now(),
      expiresAt: Date.now() + 86400000 * 7,
      maxUses: 1,
    });

    this.invites.set('FOUNDER-ALPHA-001', {
      code: 'FOUNDER-ALPHA-001',
      status: 'UNUSED',
      createdBy: 'admin_1',
      creatorRole: 'superadmin',
      boundRole: 'mesh_owner',
      createdAt: Date.now(),
      expiresAt: Date.now() + 86400000 * 30,
      maxUses: 1,
    });
  }

  public validateInviteCode(code: string, email?: string): InviteValidationResult {
    const cleanCode = code.trim().toUpperCase();

    if (cleanCode === 'SOV-ROOT-8F4A-32D9-B10E-67C5') {
      return {
        valid: false,
        error: 'LEGACY_ROOT_KEY_REVOKED: This bootstrap key has been permanently invalidated.',
      };
    }

    const invite = this.invites.get(cleanCode);

    if (!invite) {
      return { valid: false, error: 'Invalid invite code. Please verify and try again.' };
    }

    if (invite.status === 'CLAIMED') {
      return { valid: false, error: 'This single-use invite code has already been claimed.' };
    }

    if (invite.status === 'REVOKED') {
      return { valid: false, error: 'This invite code has been revoked by an administrator.' };
    }

    if (Date.now() > invite.expiresAt) {
      return { valid: false, error: 'This invite code has expired.' };
    }

    if (invite.boundEmail && email && invite.boundEmail.toLowerCase() !== email.toLowerCase()) {
      return { valid: false, error: `This invite code is restricted to ${invite.boundEmail}.` };
    }

    return {
      valid: true,
      code: invite.code,
      boundEmail: invite.boundEmail,
      boundRole: invite.boundRole || 'ordinary_user',
      expiresAt: invite.expiresAt,
    };
  }

  public async consumeInviteCode(
    code: string,
    userProof: { authProvider: string; principalId: string; displayName?: string; email?: string }
  ): Promise<{ success: boolean; inviteToken?: string; error?: string }> {
    const validation = this.validateInviteCode(code, userProof.email);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    const cleanCode = code.trim().toUpperCase();
    const invite = this.invites.get(cleanCode)!;

    // Atomically transition status to CLAIMED
    invite.status = 'CLAIMED';
    invite.claimedBy = userProof.principalId;
    invite.claimedAt = Date.now();

    // If created by an ambassador, decrement quota
    if (invite.creatorRole === 'ambassador') {
      const quota = this.ambassadorQuotas.get(invite.createdBy) || this.users.get(invite.createdBy)?.ambassadorQuota;
      if (quota) {
        quota.usedCount += 1;
        quota.remainingCount = Math.max(0, quota.totalQuota - quota.usedCount);
      }
    }

    // Provision new Mesh User
    const newUser: MeshUserRecord = {
      userId: userProof.principalId,
      displayName: userProof.displayName || `Mesh User ${userProof.principalId.slice(0, 6)}`,
      email: userProof.email || invite.boundEmail,
      role: validation.boundRole || 'ordinary_user',
      isAmbassador: false,
      status: 'ACTIVE',
      authProviders: [userProof.authProvider],
      registeredAt: Date.now(),
    };
    this.users.set(newUser.userId, newUser);

    // Audit Logging
    await auditLogger.logEvent({
      actorId: userProof.principalId,
      role: newUser.role,
      event: 'INVITE_CODE_CLAIMED',
      intentClass: 'AUTH_EVENT',
      toolsUsed: [
        {
          toolName: 'auth:invite:claim',
          params: { code: cleanCode, createdBy: invite.createdBy },
          executionResult: 'SUCCESS',
        },
      ],
      riskScore: 0.1,
    });

    const inviteToken = `inv_tok_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    return { success: true, inviteToken };
  }

  public generateInvite(
    issuerId: string,
    options: { boundEmail?: string; boundRole?: UserRole; expiresHours?: number } = {}
  ): { success: boolean; invite?: InviteCode; error?: string } {
    const issuer = this.users.get(issuerId);
    if (!issuer) {
      return { success: false, error: 'Issuer user record not found.' };
    }

    const isSuperadmin = issuer.role === 'superadmin' || issuer.role === 'mesh_owner';
    let quota = issuer.ambassadorQuota || this.ambassadorQuotas.get(issuerId);

    if (!isSuperadmin) {
      if (!issuer.isAmbassador || !quota || quota.remainingCount <= 0) {
        return { success: false, error: 'Ambassador invite quota exhausted or permissions not granted.' };
      }
    }

    const code = `INV-${Math.random().toString(36).substring(2, 6).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
    const expiresAt = Date.now() + (options.expiresHours || 168) * 3600 * 1000; // default 7 days

    const invite: InviteCode = {
      code,
      status: 'UNUSED',
      createdBy: issuerId,
      creatorRole: isSuperadmin ? issuer.role : 'ambassador',
      boundEmail: options.boundEmail,
      boundRole: options.boundRole || 'ordinary_user',
      createdAt: Date.now(),
      expiresAt,
      maxUses: 1,
    };

    this.invites.set(code, invite);

    if (!isSuperadmin && quota) {
      quota.usedCount += 1;
      quota.remainingCount = Math.max(0, quota.totalQuota - quota.usedCount);
    }

    return { success: true, invite };
  }

  public grantAmbassadorStatus(adminUserId: string, targetUserId: string, quotaAmount = 5): boolean {
    const admin = this.users.get(adminUserId);
    if (!admin || (admin.role !== 'superadmin' && admin.role !== 'mesh_owner')) {
      return false;
    }

    const target = this.users.get(targetUserId);
    if (!target) return false;

    target.isAmbassador = true;
    target.ambassadorQuota = {
      userId: targetUserId,
      isAmbassador: true,
      totalQuota: quotaAmount,
      usedCount: 0,
      remainingCount: quotaAmount,
      grantedBy: adminUserId,
      grantedAt: Date.now(),
    };

    this.ambassadorQuotas.set(targetUserId, target.ambassadorQuota);
    return true;
  }

  public revokeAmbassadorStatus(adminUserId: string, targetUserId: string): boolean {
    const admin = this.users.get(adminUserId);
    if (!admin || (admin.role !== 'superadmin' && admin.role !== 'mesh_owner')) {
      return false;
    }

    const target = this.users.get(targetUserId);
    if (!target) return false;

    target.isAmbassador = false;
    target.ambassadorQuota = undefined;
    this.ambassadorQuotas.delete(targetUserId);
    return true;
  }

  public getAllUsers(): MeshUserRecord[] {
    return Array.from(this.users.values());
  }

  public getAllInvites(): InviteCode[] {
    return Array.from(this.invites.values());
  }

  public updateUserStatus(userId: string, status: 'ACTIVE' | 'SUSPENDED'): boolean {
    const user = this.users.get(userId);
    if (!user) return false;
    user.status = status;
    return true;
  }
}

export const inviteCodeRegistry = InviteCodeRegistry.getInstance();
